// Pictures as straight RGBA pixels, for image fields: read a file (PNG, JPEG, BMP, WebP ...) through the browser's decoder, show pixels, and
// write them as a PNG for upload.

import type { Pixels } from "@fg/web-core/pdf";

const cv = (w: number, h: number): HTMLCanvasElement => { const c = document.createElement("canvas"); c.width = w; c.height = h; return c; };

/** Reads a picture file. Throws a sentence for a file the browser cannot read as a picture. */
export async function fileToPixels(file: Blob): Promise<Pixels> {
    let bm: ImageBitmap;
    try { bm = await createImageBitmap(file, { premultiplyAlpha: "none", colorSpaceConversion: "none" }); }
    catch { throw new Error("That file could not be read as a picture. Use a PNG, JPEG or BMP file."); }
    const c = cv(bm.width, bm.height);
    const g = c.getContext("2d", { willReadFrequently: true })!;
    g.drawImage(bm, 0, 0);
    bm.close();
    const d = g.getImageData(0, 0, c.width, c.height);
    return { w: c.width, h: c.height, rgba: new Uint8Array(d.data.buffer.slice(0)) };
}

const urls = new WeakMap<object, string>();

/** A data URL the browser can draw for the pixels (kept for as long as the pixels live). */
export function pixelsToUrl(p: Pixels): string {
    const hit = urls.get(p);
    if (hit) return hit;
    const c = cv(p.w, p.h);
    c.getContext("2d")!.putImageData(new ImageData(new Uint8ClampedArray(p.rgba), p.w, p.h), 0, 0);
    const u = c.toDataURL("image/png");
    urls.set(p, u);
    return u;
}

/** The pixels as a compressed PNG file. */
export async function pixelsToPng(p: Pixels): Promise<Uint8Array> {
    const c = cv(p.w, p.h);
    c.getContext("2d")!.putImageData(new ImageData(new Uint8ClampedArray(p.rgba), p.w, p.h), 0, 0);
    const blob = await new Promise<Blob | null>((r) => c.toBlob(r, "image/png"));
    if (!blob) throw new Error("The picture could not be written.");
    return new Uint8Array(await blob.arrayBuffer());
}

/** The pixels of a PNG file (as read back from a link). */
export const pngToPixels = (png: Uint8Array): Promise<Pixels> => fileToPixels(new Blob([png as BlobPart], { type: "image/png" }));
